import { useRef, useState, useEffect, useCallback } from 'react';

interface UseCanvasPanOptions {
  scrollableRef: React.RefObject<HTMLDivElement>;
  zoomLevel: number;
  isEditMode: boolean;
  enabled?: boolean;
}

interface UseCanvasPanReturn {
  isPanning: boolean;
  isPanMode: boolean;
  canvasProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    style: React.CSSProperties;
  };
}

const DRAG_THRESHOLD = 5; // pixels before we consider it a drag, not a click

// Check if an element or any ancestor matches a selector
function matchesOrContains(target: EventTarget | null, selectors: string[]): boolean {
  if (!(target instanceof HTMLElement) && !(target instanceof SVGElement)) return false;
  const el = target as Element;
  for (const selector of selectors) {
    if (el.closest(selector)) return true;
  }
  return false;
}

export function useCanvasPan({
  scrollableRef,
  zoomLevel,
  isEditMode,
  enabled = true,
}: UseCanvasPanOptions): UseCanvasPanReturn {
  // State for cursor rendering (triggers re-render)
  const [isPanning, setIsPanning] = useState(false);
  const [isPanMode, setIsPanMode] = useState(false);

  // Refs for 60fps performance (no re-renders during mousemove)
  const isPanningRef = useRef(false);
  const isPanModeRef = useRef(false);
  const isMouseDownRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const totalMovementRef = useRef(0);

  // Cleanup function ref to avoid stale closures
  const cleanupRef = useRef<(() => void) | null>(null);

  const cleanup = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    isMouseDownRef.current = false;
    if (isPanningRef.current) {
      isPanningRef.current = false;
      setIsPanning(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('canvas-panning');
    }
  }, []);

  // Mouse move handler (attached to document only during drag)
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isMouseDownRef.current || !scrollableRef.current) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    const distance = Math.abs(deltaX) + Math.abs(deltaY);

    // If space key is held, skip threshold — user explicitly wants to pan
    if (!isPanModeRef.current && distance < DRAG_THRESHOLD) {
      totalMovementRef.current = distance;
      return;
    }

    // Start panning
    if (!isPanningRef.current) {
      isPanningRef.current = true;
      setIsPanning(true);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      document.body.classList.add('canvas-panning');
    }

    // Apply scroll — native scrollLeft/scrollTop, no zoom adjustment needed
    scrollableRef.current.scrollLeft = dragStartRef.current.scrollLeft - deltaX;
    scrollableRef.current.scrollTop = dragStartRef.current.scrollTop - deltaY;

    e.preventDefault();
  }, [scrollableRef]);

  // Mouse up handler (attached to document only during drag)
  const handleMouseUp = useCallback((e: MouseEvent) => {
    const wasPanning = isPanningRef.current;

    // Clean up document listeners
    cleanup();

    // If middle button released, exit pan mode
    if (e.button === 1) {
      isPanModeRef.current = false;
      setIsPanMode(false);
    }

    // If we were panning, suppress the upcoming click event
    if (wasPanning) {
      const suppressClick = (clickEvent: MouseEvent) => {
        clickEvent.stopPropagation();
        clickEvent.preventDefault();
        document.removeEventListener('click', suppressClick, true);
      };
      // One-shot capture-phase listener to intercept click before it reaches SVG elements
      document.addEventListener('click', suppressClick, true);

      // Safety: remove after a short delay in case click never fires
      setTimeout(() => {
        document.removeEventListener('click', suppressClick, true);
      }, 100);
    }
  }, [cleanup]);

  // Mouse down handler (attached via React prop on the container)
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!enabled || isEditMode || !scrollableRef.current) return;

    // Don't intercept scrollbar, exception indicators, or popup elements
    const skipSelectors = [
      '.top-scrollbar-container',
      '.top-scrollbar-thumb',
      '.top-scrollbar-track',
      '.exception-indicator',
      '.no-exception-indicator',
      '[role="dialog"]',
      'input',
      'textarea',
      'button',
      'select',
    ];

    if (matchesOrContains(e.target, skipSelectors)) return;

    // Only left button (0) or middle button (1)
    if (e.button !== 0 && e.button !== 1) return;

    // Middle button: immediate pan mode
    if (e.button === 1) {
      e.preventDefault();
      isPanModeRef.current = true;
      setIsPanMode(true);
    }

    // Record start position
    isMouseDownRef.current = true;
    totalMovementRef.current = 0;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: scrollableRef.current.scrollLeft,
      scrollTop: scrollableRef.current.scrollTop,
    };

    // Attach document-level listeners for move and up
    const moveHandler = (ev: MouseEvent) => handleMouseMove(ev);
    const upHandler = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
      cleanupRef.current = null;
      handleMouseUp(ev);
    };

    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);

    cleanupRef.current = () => {
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
    };
  }, [enabled, isEditMode, scrollableRef, handleMouseMove, handleMouseUp]);

  // Keyboard handlers: Space key for pan mode
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;

      // Don't capture Space in inputs, textareas, or contenteditable
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }

      // Prevent page scroll
      e.preventDefault();

      if (!isPanModeRef.current) {
        isPanModeRef.current = true;
        setIsPanMode(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;

      if (isPanModeRef.current) {
        isPanModeRef.current = false;
        setIsPanMode(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [enabled]);

  // Prevent default middle-click auto-scroll behavior
  useEffect(() => {
    const container = scrollableRef.current;
    if (!container || !enabled) return;

    const preventMiddleClick = (e: MouseEvent) => {
      if (e.button === 1) {
        e.preventDefault();
      }
    };

    container.addEventListener('mousedown', preventMiddleClick);
    return () => {
      container.removeEventListener('mousedown', preventMiddleClick);
    };
  }, [scrollableRef, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('canvas-panning');
    };
  }, [cleanup]);

  // Compute cursor style
  const cursorStyle: React.CSSProperties = {
    cursor: isPanning ? 'grabbing' : isPanMode ? 'grab' : 'default',
  };

  return {
    isPanning,
    isPanMode,
    canvasProps: {
      onMouseDown,
      style: cursorStyle,
    },
  };
}
